import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchService } from '../../services/dashboardService';

const ICONS = {
  project: 'bi-building', work_progress: 'bi-card-checklist', material: 'bi-box-seam',
  labour_master: 'bi-people', material_master: 'bi-stack', user: 'bi-person',
};
const COLORS = {
  project: '#1E6FBA', work_progress: '#4299E1', material: '#48BB78',
  labour_master: '#9F7AEA', material_master: '#ED8936', user: '#2BA5A0',
};

const GlobalSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  const doSearch = useCallback(async (q) => {
    if (q.length < 2) { setResults(null); setOpen(false); return; }
    setLoading(true);
    try {
      const res = await searchService.search({ q, scope: 'all' });
      setResults(res.data.data);
      setOpen(true);
    } catch { setResults(null); }
    finally { setLoading(false); }
  }, []);

  const handleInput = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 350);
  };

  const handleSelect = (item) => {
    setOpen(false);
    setQuery('');
    navigate(item.link);
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const allItems = results ? Object.values(results.results || {}).flat() : [];

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%', maxWidth: 400 }}>
      <div style={{ position: 'relative' }}>
        <i className="bi bi-search" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#A0AEC0', fontSize: '0.85rem' }}></i>
        <input
          type="text"
          value={query}
          onChange={handleInput}
          onFocus={() => { if (results && allItems.length) setOpen(true); }}
          placeholder="Search projects, work logs, materials..."
          style={{
            width: '100%', padding: '7px 12px 7px 34px', border: '1px solid #E2E8F0', borderRadius: 8,
            fontSize: '0.8125rem', background: '#F7FAFC', outline: 'none', transition: 'border 0.2s',
          }}
        />
        {loading && <span className="spinner-border spinner-border-sm" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14 }}></span>}
      </div>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 1050,
          background: '#fff', borderRadius: 10, boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
          border: '1px solid #E2E8F0', maxHeight: 400, overflowY: 'auto',
        }}>
          {allItems.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#A0AEC0', fontSize: '0.85rem' }}>
              <i className="bi bi-search" style={{ fontSize: '1.5rem', display: 'block', marginBottom: 6 }}></i>
              No results for "{query}"
            </div>
          ) : (
            <>
              <div style={{ padding: '8px 14px', fontSize: '0.7rem', color: '#A0AEC0', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {results.totalResults} result{results.totalResults !== 1 ? 's' : ''}
              </div>
              {allItems.map((item) => (
                <div
                  key={`${item.type}-${item._id}`}
                  onClick={() => handleSelect(item)}
                  style={{
                    padding: '10px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                    borderTop: '1px solid #F7FAFC', transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#F7FAFC'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{
                    width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: (COLORS[item.type] || '#718096') + '15', color: COLORS[item.type] || '#718096', flexShrink: 0,
                  }}>
                    <i className={`bi ${ICONS[item.type] || 'bi-search'}`} style={{ fontSize: '0.9rem' }}></i>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.8125rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.title}</div>
                    <div style={{ fontSize: '0.7rem', color: '#A0AEC0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.subtitle}</div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
